import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { unwrap, type Schemas } from "@tern/api-client";
import { LoaderCircle, MessageSquare, MoreHorizontal, Pencil, Reply, Trash2 } from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { api, errorMessage, meQuery } from "@/lib/api";
import { commentThreads } from "@/lib/commentThreads";
import { formatRelative } from "@/lib/issues";
import type { ProjectData } from "@/lib/store";
import { Markdown, MarkdownEditor } from "./Markdown";
import { Avatar } from "./PropertyIcons";

type Comment = Schemas["Comment"];
type EntityType = "doc" | "task" | "issue";
const paths = { doc: "/api/docs/{id}/comments", task: "/api/tasks/{id}/comments", issue: "/api/issues/{id}/comments" } as const;

export function Comments({ d, entityType, entityId }: { d: ProjectData; entityType: EntityType; entityId: string }) {
  // Remount drafts when navigating directly between items in the same detail view.
  return <Conversation key={`${entityType}:${entityId}`} d={d} entityType={entityType} entityId={entityId} />;
}

function Conversation({ d, entityType, entityId }: { d: ProjectData; entityType: EntityType; entityId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const me = useQuery(meQuery).data?.user;
  const canWrite = !!d.project.role && !!me;
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const queryKey = ["comments", entityType, entityId];
  const q = useQuery({ queryKey, queryFn: async () => unwrap(await api.GET(paths[entityType], { params: { path: { id: entityId } } })) });
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey });
    void qc.invalidateQueries({ queryKey: [entityType, entityId, "activities"] });
  };
  const post = async (body: string, parentId?: string) => {
    unwrap(await api.POST(paths[entityType], { params: { path: { id: entityId } }, body: { body, parent_id: parentId } }));
    // The post is already durable; refetch errors must not turn it into a failed submission.
    void refresh();
  };
  const count = q.data?.filter(comment => !comment.deleted_at).length ?? 0;
  const heading = `comments-${entityType}-${entityId}`;
  const card = (comment: Comment) => <CommentCard key={comment.id} comment={comment} d={d} meId={me?.id}
    canWrite={canWrite} onChanged={() => { void refresh(); }} />;
  return <section className="mt-8 scroll-mt-6 border-t border-border pt-6" id={heading} aria-labelledby={`${heading}-title`}>
    <div className="mb-4 flex items-center gap-2">
      <MessageSquare size={15} className="text-muted" />
      <h3 id={`${heading}-title`} className="text-[14px] font-semibold">{t("comments.title")}</h3>
      {count > 0 && <span className="rounded-md bg-elevated px-1.5 text-[11px] tabular-nums text-muted">{count}</span>}
    </div>
    {q.isPending && <p role="status" className="py-3 text-[13px] text-muted">{t("common.loading")}</p>}
    {q.isError && <div role="alert" className="mb-4 flex items-center justify-between gap-3 text-[13px] text-danger"><span>{errorMessage(q.error)}</span><button className="btn h-8" onClick={() => void q.refetch()}>{t("comments.retry")}</button></div>}
    {q.isSuccess && <>
      {q.data.length === 0 && <p className="mb-4 text-[13px] text-faint">{t("comments.empty")}</p>}
      <div className="space-y-5">
        {commentThreads(q.data).map(({ root, replies }) => <div key={root.id}>
          {card(root)}
          {replies.length > 0 && <div className="ml-3 mt-3 space-y-4 border-l border-border pl-5">{replies.map(card)}</div>}
          {canWrite && !root.deleted_at && <div className="ml-9 mt-2">
            {replyTo === root.id ? <CommentComposer projectId={d.project.id} label={t("comments.replyPlaceholder")} submitLabel={t("comments.reply")} autoFocus
              onSubmit={async body => { await post(body, root.id); setReplyTo(null); }} onCancel={() => setReplyTo(null)} />
              : <button className="inline-flex items-center gap-1 text-[12px] text-muted hover:text-accent" onClick={() => setReplyTo(root.id)}><Reply size={13} />{t("comments.reply")}</button>}
          </div>}
        </div>)}
      </div>
    </>}
    {canWrite && <div className="mt-5"><CommentComposer projectId={d.project.id} label={t("comments.placeholder")} submitLabel={t("comments.post")} onSubmit={post} /></div>}
  </section>;
}

function CommentCard({ comment: c, d, meId, canWrite, onChanged }: { comment: Comment; d: ProjectData; meId?: string; canWrite: boolean; onChanged: () => void }) {
  const { t, i18n } = useTranslation();
  const [editing, setEditing] = useState<{ body: string; version: number } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState("");
  const own = meId === c.author_id;
  const canManage = canWrite && !c.deleted_at && (own || d.project.role === "admin");
  const author = d.users[c.author_id];
  const remove = async () => {
    if (deleting) return;
    setDeleting(true); setError("");
    try { unwrap(await api.DELETE("/api/comments/{id}", { params: { path: { id: c.id } } })); setConfirmDelete(false); onChanged(); }
    catch (e) { setError(errorMessage(e)); }
    finally { setDeleting(false); }
  };
  return <article className="group min-w-0" aria-label={t("comments.by", { name: author?.name ?? t("comments.unknownAuthor") })}>
    <div className="mb-1.5 flex items-center gap-2 text-[12px]">
      <Avatar user={author} size={26} />
      <span className="font-medium text-fg">{author?.name ?? t("comments.unknownAuthor")}</span>
      <time dateTime={c.created_at} title={new Date(c.created_at).toLocaleString(i18n.language)} className="text-faint">{formatRelative(c.created_at, i18n.language)}</time>
      {c.edited_at && !c.deleted_at && <span className="text-faint">{t("comments.edited")}</span>}
      {canManage && !editing && <DropdownMenu.Root><DropdownMenu.Trigger asChild>
        <button className="btn btn-ghost ml-auto h-6 w-6 justify-center p-0 text-faint hover:text-fg" aria-label={t("comments.actions")}><MoreHorizontal size={16} /></button>
      </DropdownMenu.Trigger><DropdownMenu.Portal><DropdownMenu.Content align="end" sideOffset={4} className="z-50 min-w-[140px] rounded-lg border border-border bg-elevated p-1 shadow-xl">
        {own && <DropdownMenu.Item className="flex cursor-default items-center gap-2 rounded-md px-2 py-1.5 text-[13px] outline-none data-[highlighted]:bg-hover" onSelect={() => { setEditing({ body: c.body, version: c.version }); setConfirmDelete(false); }}><Pencil size={13} />{t("common.edit")}</DropdownMenu.Item>}
        <DropdownMenu.Item className="flex cursor-default items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-danger outline-none data-[highlighted]:bg-hover" onSelect={() => setConfirmDelete(true)}><Trash2 size={13} />{t("common.delete")}</DropdownMenu.Item>
      </DropdownMenu.Content></DropdownMenu.Portal></DropdownMenu.Root>}
    </div>
    <div className="ml-9 min-w-0 break-words">
      {c.deleted_at ? <p className="text-[13px] italic text-faint">{t("comments.deleted")}</p> : editing ?
        <CommentComposer projectId={d.project.id} initialValue={editing.body} label={t("comments.editPlaceholder")} submitLabel={t("common.save")} autoFocus
          onCancel={() => setEditing(null)} onSubmit={async body => {
            unwrap(await api.PATCH("/api/comments/{id}", { params: { path: { id: c.id } }, body: { body, version: editing.version } }));
            setEditing(null); onChanged();
          }} /> : <Markdown source={c.body} />}
      {confirmDelete && !c.deleted_at && <div className="mt-3 flex items-center gap-2 text-[12px]">
        <span className="mr-auto text-muted">{t("comments.deleteConfirm")}</span>
        <button className="btn h-7" disabled={deleting} onClick={() => { setConfirmDelete(false); setError(""); }}>{t("common.cancel")}</button>
        <button className="btn h-7 text-danger" disabled={deleting} onClick={() => void remove()}>{deleting ? <LoaderCircle className="animate-spin" size={13} /> : t("common.delete")}</button>
      </div>}
      {error && <p role="alert" className="mt-2 text-[12px] text-danger">{error}</p>}
    </div>
  </article>;
}

function CommentComposer({ projectId, label, submitLabel, initialValue = "", autoFocus, onSubmit, onCancel }: {
  projectId: string; label: string; submitLabel: string; initialValue?: string; autoFocus?: boolean;
  onSubmit: (body: string) => Promise<void>; onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const [body, setBody] = useState(initialValue);
  const [busy, setBusy] = useState(false);
  const [editorBusy, setEditorBusy] = useState(false);
  const [error, setError] = useState("");
  const sending = useRef(false);
  const tooLong = new TextEncoder().encode(body).length > 50_000;
  const submit = async () => {
    if (!body.trim() || sending.current || editorBusy || tooLong) return;
    sending.current = true; setBusy(true); setError("");
    try { await onSubmit(body); setBody(""); }
    catch (e) { setError(errorMessage(e)); }
    finally { sending.current = false; setBusy(false); }
  };
  return <div>
    <MarkdownEditor projectId={projectId} value={body} onChange={setBody} onBusyChange={setEditorBusy} placeholder={label} minRows={2} disabled={busy} autoFocus={autoFocus} onSave={() => void submit()} />
    {(error || tooLong) && <p role="alert" className="mt-2 text-[12px] text-danger">{tooLong ? t("comments.tooLong") : error}</p>}
    <div className="mt-2 flex items-center justify-end gap-2">
      {onCancel && <button className="btn btn-ghost h-8 text-[12px]" disabled={busy || editorBusy} onClick={onCancel}>{t("common.cancel")}</button>}
      <button className="btn btn-primary h-8 text-[12px]" disabled={!body.trim() || busy || editorBusy || tooLong} onClick={() => void submit()}>
        {busy && <LoaderCircle size={13} className="animate-spin" />}{submitLabel}
      </button>
    </div>
  </div>;
}
